import {
  EditorState,
  Modifier,
  SelectionState,
  convertFromRaw,
  convertToRaw,
} from 'draft-js';
import { extractInlineStyle } from 'draftjs-utils';

export const FONT_FAMILY_OPTIONS = [
  'Tinos',
  'Roboto',
  'Courier Prime',
  'Courier New',
  'Arial',
  'Georgia',
  'Impact',
  'Tahoma',
  'Times New Roman',
  'Verdana',
];

export const FONT_SIZE_OPTIONS = [
  8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96,
];

const FONT_FAMILY_STYLE_PREFIX = 'fontfamily-';
const FONT_SIZE_STYLE_PREFIX = 'fontsize-';
const FONT_SIZE_STYLE_PATTERN = /^fontsize-/i;
const EDITOR_TAB_WIDTH = 4;
// One tab level from the reverted margin-left experiment (36px ~= 4 nbsp).
const LEGACY_TAB_INDENT_PX = 36;

const parseMarginLeftValue = (value) => {
  if (value == null || value === '') {
    return 0;
  }

  const match = String(value)
    .trim()
    .toLowerCase()
    .match(/^([\d.]+)\s*(px|pt|em|rem)?$/);
  if (!match) {
    return 0;
  }

  const amount = parseFloat(match[1]);
  const unit = match[2] || 'px';
  if (unit === 'pt') {
    return amount * (96 / 72);
  }
  if (unit === 'em' || unit === 'rem') {
    return amount * 16;
  }
  return amount;
};

const marginPxToNbspCount = (px) => {
  if (!px || px <= 0) {
    return 0;
  }

  const tabLevels = Math.max(1, Math.round(px / LEGACY_TAB_INDENT_PX));
  return tabLevels * EDITOR_TAB_WIDTH;
};

/** Undo margin-left tab indent from the reverted PDF experiment. */
export const demoteMarginLeftInHtml = (html) => {
  if (!html) {
    return html;
  }

  return html.replace(
    /<(p|div|h[1-6]|li|blockquote)(\s[^>]*)>([\s\S]*?)<\/\1>/gi,
    (match, tag, attrs, inner) => {
      const styleMatch = attrs.match(/\sstyle="([^"]*)"/i);
      if (!styleMatch) {
        return match;
      }

      const marginMatch = styleMatch[1].match(/margin-left\s*:\s*([^;]+)/i);
      if (!marginMatch) {
        return match;
      }

      const nbspCount = marginPxToNbspCount(
        parseMarginLeftValue(marginMatch[1].trim())
      );
      if (nbspCount <= 0) {
        return match;
      }

      let newStyle = styleMatch[1]
        .replace(/margin-left\s*:\s*[^;]+;?\s*/gi, '')
        .replace(/;\s*$/, '')
        .trim();
      let newAttrs = attrs.replace(/\sstyle="[^"]*"/i, '');
      if (newStyle) {
        newAttrs += ` style="${newStyle}"`;
      }

      const leading = '&#160;'.repeat(nbspCount);
      return `<${tag}${newAttrs}>${leading}${inner}</${tag}>`;
    }
  );
};

export const demoteMarginLeftInEditorState = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  let content = editorState.getCurrentContent();
  let changed = false;

  content.getBlockMap().forEach((block, blockKey) => {
    const data = block.getData();
    const marginLeft = data.get('margin-left') || data.get('marginLeft');
    if (!marginLeft) {
      return;
    }

    const nbspCount = marginPxToNbspCount(parseMarginLeftValue(marginLeft));
    changed = true;

    if (nbspCount > 0) {
      const blockSelection = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: 0,
        focusOffset: 0,
      });
      content = Modifier.insertText(
        content,
        blockSelection,
        '\u00A0'.repeat(nbspCount)
      );
    }

    content = Modifier.setBlockData(
      content,
      SelectionState.createEmpty(blockKey),
      data.delete('margin-left').delete('marginLeft')
    );
  });

  if (!changed) {
    return editorState;
  }

  return EditorState.set(editorState, { currentContent: content });
};

export const fontFamilyToStyleName = (fontFamily) =>
  `${FONT_FAMILY_STYLE_PREFIX}${String(fontFamily)
    .trim()
    .replace(/\s+/g, '-')}`;

const normalizeFontFamilyKey = (value) =>
  String(value).trim().toLowerCase().replace(/[\s-]+/g, '');

export const normalizeFontFamilyStyleName = (style) => {
  if (!style.startsWith(FONT_FAMILY_STYLE_PREFIX)) {
    return style;
  }

  const rawValue = style.substring(FONT_FAMILY_STYLE_PREFIX.length);
  const match = FONT_FAMILY_OPTIONS.find(
    (fontFamily) =>
      normalizeFontFamilyKey(fontFamily) === normalizeFontFamilyKey(rawValue)
  );

  return match ? fontFamilyToStyleName(match) : style;
};

const collectFontFamilyStyles = (content) => {
  const styles = new Set();
  const raw = convertToRaw(content);

  raw.blocks.forEach((block) => {
    block.inlineStyleRanges.forEach((range) => {
      if (range.style.startsWith(FONT_FAMILY_STYLE_PREFIX)) {
        styles.add(range.style);
      }
    });
  });

  return styles;
};

export const fontSizeToStyleName = (fontSize) =>
  `${FONT_SIZE_STYLE_PREFIX}${String(fontSize).trim()}`;

export const normalizeFontSizeStyleName = (style) => {
  const match = String(style).match(/^fontsize-(.+)$/i);
  if (!match) {
    return style;
  }

  const sizeValue = match[1].replace(/px$/i, '').trim();
  return fontSizeToStyleName(sizeValue);
};

const collectFontSizeStyles = (content) => {
  const styles = new Set();
  const raw = convertToRaw(content);

  raw.blocks.forEach((block) => {
    block.inlineStyleRanges.forEach((range) => {
      if (FONT_SIZE_STYLE_PATTERN.test(range.style)) {
        styles.add(range.style);
      }
    });
  });

  return styles;
};

const parseBlockFontSizeValue = (value) => {
  if (value == null || value === '') {
    return null;
  }

  const match = String(value)
    .trim()
    .toLowerCase()
    .match(/^([\d.]+)\s*(px|pt)?$/);
  if (!match) {
    return null;
  }

  let amount = parseFloat(match[1]);
  if (match[2] === 'pt') {
    amount = Math.round(amount * (96 / 72));
  } else {
    amount = Math.round(amount);
  }

  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

const blockHasInlineFontSize = (block) => {
  const text = block.getText();
  if (!text.length) {
    return false;
  }

  for (let index = 0; index < text.length; index += 1) {
    if (
      block
        .getInlineStyleAt(index)
        .some((style) => FONT_SIZE_STYLE_PATTERN.test(style))
    ) {
      return true;
    }
  }

  return false;
};

const stripBlockFontSizeData = (content, blockKey) => {
  const block = content.getBlockForKey(blockKey);
  const data = block.getData();
  if (!data.get('font-size') && !data.get('fontSize')) {
    return content;
  }

  return Modifier.setBlockData(
    content,
    SelectionState.createEmpty(blockKey),
    data.delete('font-size').delete('fontSize')
  );
};

/** Promote legacy block-level font-size data to inline fontsize-* styles. */
export const promoteBlockFontSizeToInline = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  let content = editorState.getCurrentContent();
  let changed = false;

  content.getBlockMap().forEach((block, blockKey) => {
    const data = block.getData();
    const fontSizeValue = parseBlockFontSizeValue(
      data.get('font-size') || data.get('fontSize')
    );
    if (!fontSizeValue || blockHasInlineFontSize(block)) {
      return;
    }

    const text = block.getText();
    if (!text.length) {
      return;
    }

    changed = true;
    content = Modifier.applyInlineStyle(
      content,
      SelectionState.createEmpty(blockKey).merge({
        anchorOffset: 0,
        focusOffset: text.length,
      }),
      fontSizeToStyleName(fontSizeValue)
    );
    content = stripBlockFontSizeData(content, blockKey);
  });

  if (!changed) {
    return editorState;
  }

  return EditorState.set(editorState, { currentContent: content });
};

const normalizeWhitespaceRun = (value) =>
  value
    .replace(/\t/g, '\u00A0'.repeat(EDITOR_TAB_WIDTH))
    .replace(/ /g, '\u00A0');

const mergeWhitespaceRanges = (ranges) => {
  if (!ranges.length) {
    return [];
  }

  const sorted = [...ranges].sort((left, right) => left.start - right.start);
  const merged = [{ ...sorted[0] }];

  for (let index = 1; index < sorted.length; index += 1) {
    const current = sorted[index];
    const previous = merged[merged.length - 1];
    if (current.start <= previous.end) {
      previous.end = Math.max(previous.end, current.end);
    } else {
      merged.push({ ...current });
    }
  }

  return merged;
};

/**
 * Intentional spacing only (not natural single word separators):
 * - leading / trailing indent on a line
 * - indent after a line break
 * - runs of 2+ spaces/tabs/nbsp (extra gaps between words, address padding, etc.)
 */
export const findIntentionalWhitespaceRanges = (text) => {
  const ranges = [];
  const addRange = (start, end) => {
    if (end > start) {
      ranges.push({ start, end });
    }
  };

  if (!text) {
    return ranges;
  }

  const leading = text.match(/^[\t \u00A0]+/);
  if (leading) {
    addRange(0, leading[0].length);
  }

  let lineBreakMatch = /\n[\t \u00A0]+/g;
  let match = lineBreakMatch.exec(text);
  while (match) {
    addRange(match.index + 1, match.index + match[0].length);
    match = lineBreakMatch.exec(text);
  }

  const trailing = /[ \t\u00A0]+(?=\n|$)/g;
  match = trailing.exec(text);
  while (match) {
    addRange(match.index, match.index + match[0].length);
    match = trailing.exec(text);
  }

  // Extra gaps between words / address padding (2+ whitespace chars)
  const multiSpace = /[ \t\u00A0]{2,}/g;
  match = multiSpace.exec(text);
  while (match) {
    addRange(match.index, match.index + match[0].length);
    match = multiSpace.exec(text);
  }

  return mergeWhitespaceRanges(ranges);
};

/** True if offset falls inside any [start, end) range. */
const isOffsetInRanges = (offset, ranges) =>
  ranges.some((range) => offset >= range.start && offset < range.end);

/** Copy inline styles from the first non-whitespace character to leading indent on each line. */
export const ensureIndentInheritsFollowingStyles = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  let content = editorState.getCurrentContent();
  let changed = false;

  content.getBlockMap().forEach((block, blockKey) => {
    const text = block.getText();
    let lineStart = 0;

    for (let index = 0; index <= text.length; index += 1) {
      if (index < text.length && text[index] !== '\n') {
        continue;
      }

      const lineEnd = index;
      const line = text.slice(lineStart, lineEnd);
      const leadingMatch = line.match(/^[\t \u00A0]+/);

      if (leadingMatch && leadingMatch[0].length < line.length) {
        const indentLength = leadingMatch[0].length;
        const styleSourceOffset = lineStart + indentLength;
        const stylesToApply = content
          .getBlockForKey(blockKey)
          .getInlineStyleAt(styleSourceOffset);

        stylesToApply.forEach((style) => {
          let needsApply = false;
          for (
            let offset = lineStart;
            offset < lineStart + indentLength;
            offset += 1
          ) {
            if (!content.getBlockForKey(blockKey).getInlineStyleAt(offset).has(style)) {
              needsApply = true;
              break;
            }
          }

          if (needsApply) {
            content = Modifier.applyInlineStyle(
              content,
              SelectionState.createEmpty(blockKey).merge({
                anchorOffset: lineStart,
                focusOffset: lineStart + indentLength,
              }),
              style
            );
            changed = true;
          }
        });
      }

      lineStart = index + 1;
    }
  });

  if (!changed) {
    return editorState;
  }

  return EditorState.set(editorState, { currentContent: content });
};

/**
 * Normalize editor whitespace:
 * - intentional runs → NBSP (survives HTML collapse / Preview)
 * - leftover single NBSPs between words → normal space (word wrap)
 */
export const normalizeEditorWhitespace = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  let content = editorState.getCurrentContent();
  let changed = false;

  content.getBlockMap().forEach((block, blockKey) => {
    const applyReplace = (start, end, replacement) => {
      const selection = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: start,
        focusOffset: end,
      });
      content = Modifier.replaceText(content, selection, replacement);
      changed = true;
    };

    // Pass 1: encode intentional runs as NBSP (process from end so offsets stay valid)
    let text = content.getBlockForKey(blockKey).getText();
    const intentionalRanges = findIntentionalWhitespaceRanges(text);
    [...intentionalRanges]
      .sort((left, right) => right.start - left.start)
      .forEach((range) => {
        text = content.getBlockForKey(blockKey).getText();
        if (range.end > text.length) {
          return;
        }
        const original = text.slice(range.start, range.end);
        const normalized = normalizeWhitespaceRun(original);
        if (normalized !== original) {
          applyReplace(range.start, range.end, normalized);
        }
      });

    // Pass 2: demote leftover single NBSPs (legacy over-encoding) to normal spaces
    text = content.getBlockForKey(blockKey).getText();
    const keepRanges = findIntentionalWhitespaceRanges(text);
    for (let index = text.length - 1; index >= 0; index -= 1) {
      if (text[index] === '\u00A0' && !isOffsetInRanges(index, keepRanges)) {
        applyReplace(index, index + 1, ' ');
      }
    }
  });

  if (!changed) {
    return editorState;
  }

  return EditorState.set(editorState, { currentContent: content });
};

/** @deprecated Use normalizeEditorWhitespace */
export const normalizeEditorLeadingIndent = normalizeEditorWhitespace;

/** Normalize html-to-draftjs font keys like fontfamily-Times New Roman. */
export const normalizeEditorFontFamilyStyles = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  const raw = convertToRaw(editorState.getCurrentContent());
  let modified = false;

  raw.blocks.forEach((block) => {
    block.inlineStyleRanges = block.inlineStyleRanges.map((range) => {
      if (!range.style.startsWith(FONT_FAMILY_STYLE_PREFIX)) {
        return range;
      }

      const normalized = normalizeFontFamilyStyleName(range.style);
      if (normalized !== range.style) {
        modified = true;
      }
      return { ...range, style: normalized };
    });
  });

  if (!modified) {
    return editorState;
  }

  const content = convertFromRaw(raw);
  return EditorState.set(editorState, { currentContent: content });
};

/** Normalize html-to-draftjs font size keys like fontsize-16px. */
export const normalizeEditorFontSizeStyles = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  const raw = convertToRaw(editorState.getCurrentContent());
  let modified = false;

  raw.blocks.forEach((block) => {
    block.inlineStyleRanges = block.inlineStyleRanges.map((range) => {
      if (!FONT_SIZE_STYLE_PATTERN.test(range.style)) {
        return range;
      }

      const normalized = normalizeFontSizeStyleName(range.style);
      if (normalized !== range.style) {
        modified = true;
      }
      return { ...range, style: normalized };
    });
  });

  if (!modified) {
    return editorState;
  }

  const content = convertFromRaw(raw);
  return EditorState.set(editorState, { currentContent: content });
};

const blockHasInlineFontFamily = (block) => {
  const text = block.getText();
  if (!text.length) {
    return false;
  }

  for (let index = 0; index < text.length; index += 1) {
    if (
      block
        .getInlineStyleAt(index)
        .some((style) => style.startsWith(FONT_FAMILY_STYLE_PREFIX))
    ) {
      return true;
    }
  }

  return false;
};

const stripBlockFontFamilyData = (content, blockKey) => {
  const block = content.getBlockForKey(blockKey);
  const data = block.getData();
  if (!data.get('font-family') && !data.get('fontFamily')) {
    return content;
  }

  return Modifier.setBlockData(
    content,
    SelectionState.createEmpty(blockKey),
    data.delete('font-family').delete('fontFamily')
  );
};

const getBlockKeysInRange = (content, startKey, endKey) => {
  const blockKeys = content.getBlockMap().keySeq().toArray();
  const startIndex = blockKeys.indexOf(startKey);
  const endIndex = blockKeys.indexOf(endKey);
  if (startIndex === -1 || endIndex === -1) {
    return [];
  }
  return blockKeys.slice(startIndex, endIndex + 1);
};

/** Apply toolbar font family and remove legacy fontfamily-* variants from selection. */
export const applyFontFamilyToSelection = (editorState, fontFamily) => {
  if (!editorState || !fontFamily) {
    return editorState;
  }

  const styleName = fontFamily.startsWith(FONT_FAMILY_STYLE_PREFIX)
    ? normalizeFontFamilyStyleName(fontFamily)
    : fontFamilyToStyleName(fontFamily);
  const selection = editorState.getSelection();
  let content = editorState.getCurrentContent();

  if (selection.isCollapsed()) {
    let inlineStyle = editorState.getCurrentInlineStyle();
    collectFontFamilyStyles(content).forEach((style) => {
      inlineStyle = inlineStyle.remove(style);
    });
    inlineStyle = inlineStyle.add(styleName);
    return EditorState.setInlineStyleOverride(editorState, inlineStyle);
  }

  collectFontFamilyStyles(content).forEach((style) => {
    content = Modifier.removeInlineStyle(content, selection, style);
  });
  content = Modifier.applyInlineStyle(content, selection, styleName);

  const blockKeys = getBlockKeysInRange(
    content,
    selection.getStartKey(),
    selection.getEndKey()
  );
  blockKeys.forEach((blockKey) => {
    content = stripBlockFontFamilyData(content, blockKey);
  });

  const nextState = EditorState.push(
    editorState,
    content,
    'change-inline-style'
  );
  return EditorState.forceSelection(nextState, selection);
};

/** Apply toolbar font size and remove legacy fontsize-* variants from selection. */
export const applyFontSizeToSelection = (editorState, fontSize) => {
  if (!editorState || !fontSize) {
    return editorState;
  }

  const styleName = FONT_SIZE_STYLE_PATTERN.test(fontSize)
    ? normalizeFontSizeStyleName(fontSize)
    : fontSizeToStyleName(fontSize);
  const selection = editorState.getSelection();
  let content = editorState.getCurrentContent();

  if (selection.isCollapsed()) {
    let inlineStyle = editorState.getCurrentInlineStyle();
    collectFontSizeStyles(content).forEach((style) => {
      inlineStyle = inlineStyle.remove(style);
    });
    inlineStyle = inlineStyle.add(styleName);
    return EditorState.setInlineStyleOverride(editorState, inlineStyle);
  }

  collectFontSizeStyles(content).forEach((style) => {
    content = Modifier.removeInlineStyle(content, selection, style);
  });
  content = Modifier.applyInlineStyle(content, selection, styleName);

  const blockKeys = getBlockKeysInRange(
    content,
    selection.getStartKey(),
    selection.getEndKey()
  );
  blockKeys.forEach((blockKey) => {
    content = stripBlockFontSizeData(content, blockKey);
  });

  const nextState = EditorState.push(
    editorState,
    content,
    'change-inline-style'
  );
  return EditorState.forceSelection(nextState, selection);
};

const stripBlockLevelFontFamily = (editorState) => {
  const content = editorState.getCurrentContent();
  let blockMap = content.getBlockMap();
  let modified = false;

  blockMap.forEach((block, blockKey) => {
    if (!blockHasInlineFontFamily(block)) {
      return;
    }

    const data = block.getData();
    if (!data.get('font-family') && !data.get('fontFamily')) {
      return;
    }

    blockMap = blockMap.set(
      blockKey,
      block.set('data', data.delete('font-family').delete('fontFamily'))
    );
    modified = true;
  });

  if (!modified) {
    return editorState;
  }

  return EditorState.push(
    editorState,
    content.merge({ blockMap }),
    'change-block-data'
  );
};

/** Used when exporting editor content to HTML so inline font families are preserved. */
export const prepareEditorStateForHtmlExport = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return editorState;
  }

  let preparedState = demoteMarginLeftInEditorState(editorState);
  preparedState = normalizeEditorWhitespace(preparedState);
  preparedState = ensureIndentInheritsFollowingStyles(preparedState);
  preparedState = normalizeEditorFontFamilyStyles(preparedState);
  preparedState = normalizeEditorFontSizeStyles(preparedState);
  preparedState = extractInlineStyle(preparedState) || preparedState;
  preparedState = stripBlockLevelFontFamily(preparedState);
  preparedState = normalizeEditorFontFamilyStyles(preparedState);
  preparedState = normalizeEditorFontSizeStyles(preparedState);
  preparedState = ensureIndentInheritsFollowingStyles(preparedState);
  preparedState = normalizeEditorWhitespace(preparedState);

  if (preparedState.getCurrentContent() === editorState.getCurrentContent()) {
    return editorState;
  }

  return preparedState;
};

export const serializeEditorContent = (editorState) => {
  if (!editorState || !editorState.getCurrentContent) {
    return '';
  }

  return JSON.stringify(convertToRaw(editorState.getCurrentContent()));
};

export const editorContentEquals = (leftState, rightState) => {
  if (!leftState || !rightState) {
    return leftState === rightState;
  }

  if (leftState.getCurrentContent() === rightState.getCurrentContent()) {
    return true;
  }

  return serializeEditorContent(leftState) === serializeEditorContent(rightState);
};
